export type GhlAuthMode = 'oauth' | 'pit';

export type GhlAuth = {
  token: string;
  locationId: string;
  mode: GhlAuthMode;
  /** Used by higherlevel-proxy to refresh+persist OAuth without matching on a stale access token. */
  userId?: string;
};

export type ZoneDmIntegration = {
  user_id?: string | null;
  access_token?: string | null;
  location_id?: string | null;
  config?: {
    pit?: string;
    locationId?: string;
  } | null;
};

export function resolveGhlAuth(integration: ZoneDmIntegration | null | undefined): GhlAuth | null {
  if (!integration) return null;

  const locationId = integration.location_id || integration.config?.locationId;
  if (!locationId) return null;

  const userId = integration.user_id || undefined;

  if (integration.access_token) {
    return { token: integration.access_token, locationId, mode: 'oauth', userId };
  }

  const pit = integration.config?.pit;
  if (pit) {
    return { token: pit, locationId, mode: 'pit', userId };
  }

  return null;
}

export function toZoneDmCredentials(auth: GhlAuth): {
  pit: string;
  locationId: string;
  userId?: string;
} {
  return { pit: auth.token, locationId: auth.locationId, userId: auth.userId };
}

export const GHL_AUTH_MISSING_MESSAGE =
  'ZoneDM auth not configured (connect OAuth or add PIT + location ID)';

export type OAuthTokenHealth = {
  state: 'ok' | 'expiring_soon' | 'expired_refreshable' | 'expired_needs_reconnect' | 'missing_expiry';
  label: string;
  detail: string;
  badgeClass: string;
  /** Highlight reconnect CTA */
  needsReconnect: boolean;
};

const EXPIRING_SOON_MS = 2 * 60 * 60 * 1000; // warn only near end of ~24h access lifetime

/** UI helper: surface OAuth access-token expiry on Integration Setup. */
export function getOAuthTokenHealth(
  integration:
    | {
        access_token?: string | null;
        refresh_token?: string | null;
        token_expiry?: string | null;
      }
    | null
    | undefined
): OAuthTokenHealth | null {
  if (!integration?.access_token) return null;

  const hasRefresh = !!integration.refresh_token;
  const expiryRaw = integration.token_expiry;
  if (!expiryRaw) {
    return {
      state: 'missing_expiry',
      label: 'Connected via OAuth',
      detail: 'No token expiry recorded. Reconnect if HighLevel API calls start failing.',
      badgeClass: 'bg-yellow-100 text-yellow-800',
      needsReconnect: false,
    };
  }

  const expiry = new Date(expiryRaw);
  if (Number.isNaN(expiry.getTime())) {
    return {
      state: 'missing_expiry',
      label: 'Connected via OAuth',
      detail: 'Token expiry is invalid. Reconnect to refresh credentials.',
      badgeClass: 'bg-yellow-100 text-yellow-800',
      needsReconnect: true,
    };
  }

  const msLeft = expiry.getTime() - Date.now();
  const when = expiry.toLocaleString();

  if (msLeft <= 0) {
    if (hasRefresh) {
      return {
        state: 'expired_refreshable',
        label: 'Access token expired',
        detail: `Expired ${when}. Next HighLevel call should auto-refresh and save a new token. Reconnect if that fails.`,
        badgeClass: 'bg-amber-100 text-amber-900',
        needsReconnect: false,
      };
    }
    return {
      state: 'expired_needs_reconnect',
      label: 'OAuth expired',
      detail: `Access token expired ${when} and no refresh token is stored. Reconnect OAuth.`,
      badgeClass: 'bg-red-100 text-red-800',
      needsReconnect: true,
    };
  }

  // HighLevel access tokens are ~24h by design. Only warn in the last couple hours.
  if (msLeft <= EXPIRING_SOON_MS) {
    const hours = Math.max(1, Math.ceil(msLeft / (60 * 60 * 1000)));
    return {
      state: 'expiring_soon',
      label: `Expires in ~${hours}h`,
      detail: `Access token expires ${when}. Auto-refresh should renew it on the next API call.`,
      badgeClass: 'bg-amber-100 text-amber-900',
      needsReconnect: false,
    };
  }

  return {
    state: 'ok',
    label: 'Connected via OAuth',
    detail: hasRefresh
      ? `Access token renews automatically (current token valid until ${when}).`
      : `Access token valid until ${when}. No refresh token stored — reconnect before it expires.`,
    badgeClass: 'bg-green-100 text-green-800',
    needsReconnect: !hasRefresh,
  };
}
